import client from './client';
import { normalizePayPeriods } from '../Pages/v2/employeePortal/portalModel';
import { normalizeActivity } from '../Pages/v2/employeePortal/activityModel';
import type {
  PortalAccountCode,
  PortalActivityResponse,
  PortalHoliday,
  PortalHourlyRow,
  PortalMealWaiverDates,
  PortalPayPeriod,
  PortalPhase,
  PortalProjectSearchResult,
  PortalPunchAddResponse,
  PortalRecentProject,
  PortalSelf,
  PortalSwitchResponse,
  PortalTimeOffBalance,
  PortalTimeOffLedgerPage,
  PortalTimeOffRequest,
  PortalTimeOffRequestBody,
  PortalTimeOffRequestOptions,
  PortalTimeSummary,
  PortalTimesheetQuestion,
  PortalTimesheetResponse,
  PortalTimesheetStatus,
  PortalTimesheetSubmitBody,
} from '../Pages/v2/employeePortal/portalTypes';
import type { PendingPunch } from '../Pages/v2/employeePortal/punchStore';

export { getTokenStatus, reacquireTokenInteractive } from './client';
export type { TokenStatus } from './client';
export { describePortalError, isRetryable, PORTAL_ERROR_MESSAGES } from '../Pages/v2/employeePortal/portalErrors';
export type { PortalErrorInfo, PortalErrorKind } from '../Pages/v2/employeePortal/portalErrors';

/**
 * Employee Time Portal API (P-TAS-20 Phase 3) — every call goes to /portal/*, which the
 * API scopes to the signed-in employee (MSAL token → AdminEmployee → Employee). NEVER call
 * /admin/* from the portal: an employee-only user is refused there (ADMIN_GATE=enforce).
 * Errors are left as axios errors; classify them with describePortalError.
 */

const P = '/portal';

/** GET /portal/self — the employee, role, this pay period's punches, the open punch. */
export async function getPortalSelf(): Promise<PortalSelf> {
  const res = await client.get<PortalSelf>(`${P}/self`);
  return res.data;
}

/** GET /portal/timesheet/status?payPeriodID= */
export async function getPortalTimesheetStatus(payPeriodID: number): Promise<PortalTimesheetStatus> {
  const res = await client.get<PortalTimesheetStatus>(`${P}/timesheet/status`, { params: { payPeriodID } });
  return res.data;
}

/** GET /portal/payPeriods/getAroundDate?date= (4 weeks back, 6 ahead), normalized. */
export async function getPortalPayPeriodsAround(ymd: string): Promise<PortalPayPeriod[]> {
  const res = await client.get(`${P}/payPeriods/getAroundDate`, { params: { date: ymd } });
  return normalizePayPeriods(res.data);
}

/** GET /portal/timeEntryHourly/:employeeID/:payPeriodID — the employee's punches for a week. */
export async function getPortalPeriodHourly(employeeID: number, payPeriodID: number): Promise<PortalHourlyRow[]> {
  const res = await client.get<PortalHourlyRow[]>(`${P}/timeEntryHourly/${employeeID}/${payPeriodID}`);
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /portal/projects/recent?limit= */
export async function getPortalRecentProjects(limit = 10): Promise<PortalRecentProject[]> {
  const res = await client.get<PortalRecentProject[]>(`${P}/projects/recent`, { params: { limit } });
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /portal/projects/search?searchValue= (the server forces employeeID to the signed-in employee). */
export async function searchPortalProjects(searchValue: string, employeeID: number): Promise<PortalProjectSearchResult[]> {
  const res = await client.get<PortalProjectSearchResult[]>(`${P}/projects/search`, {
    params: { searchValue, employeeID },
  });
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /portal/projects/:id/getActivePhases */
export async function getPortalProjectPhases(projectID: number): Promise<PortalPhase[]> {
  const res = await client.get<PortalPhase[]>(`${P}/projects/${projectID}/getActivePhases`);
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /portal/projects/:projectId/phases/:phaseId/getAccountCodes */
export async function getPortalPhaseAccountCodes(projectID: number, projectPhaseID: number): Promise<PortalAccountCode[]> {
  const res = await client.get<PortalAccountCode[]>(`${P}/projects/${projectID}/phases/${projectPhaseID}/getAccountCodes`);
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /portal/timeOff/holidays?startDate&endDate (YYYY-MM-DD) */
export async function getPortalHolidays(startYmd: string, endYmd: string): Promise<PortalHoliday[]> {
  const res = await client.get<PortalHoliday[]>(`${P}/timeOff/holidays`, { params: { startDate: startYmd, endDate: endYmd } });
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /portal/timeOff/requests */
export async function getPortalTimeOffRequests(): Promise<PortalTimeOffRequest[]> {
  const res = await client.get<PortalTimeOffRequest[]>(`${P}/timeOff/requests`);
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * Sends a pending punch exactly as stored (method, URL, ORIGINAL body with the tap time and
 * clientPunchID). Callers check getTokenStatus() first: a punch is never sent without one.
 */
export async function sendPendingPunch(punch: PendingPunch): Promise<PortalPunchAddResponse | PortalSwitchResponse> {
  const url = `${P}${punch.request.url}`;
  const res = punch.request.method === 'put'
    ? await client.put(url, punch.request.body)
    : await client.post(url, punch.request.body);
  return res.data as PortalPunchAddResponse | PortalSwitchResponse;
}

/** The URL (under /portal) for each punch kind. */
export const PUNCH_URL = {
  add: '/timeEntryHourly',
  update: (timeEntryHourlyID: number) => `/timeEntryHourly/${timeEntryHourlyID}`,
  switch: (timeEntryHourlyID: number) => `/timeEntryHourly/${timeEntryHourlyID}/switch`,
};

/* ------------------------------------------------------------------ My Timesheet (App-2) */

/** GET /portal/timesheet?employeeID&payPeriodID — the week's TimeEntry + TimeEntryHourly rows and whether it was submitted. */
export async function getPortalTimesheet(employeeID: number, payPeriodID: number): Promise<PortalTimesheetResponse> {
  const res = await client.get<PortalTimesheetResponse>(`${P}/timesheet`, { params: { employeeID, payPeriodID } });
  const d = res.data;
  return {
    ...d,
    timeEntries: Array.isArray(d?.timeEntries) ? d.timeEntries : [],
    timeEntriesHourly: Array.isArray(d?.timeEntriesHourly) ? d.timeEntriesHourly : [],
  };
}

/**
 * POST /portal/timesheet/getTimeSummary. Send EVERY row ID of the week (the server
 * requires an exact match; it leaves still-open rows out of the calc itself).
 */
export async function getPortalTimeSummary(body: {
  employeeID: number;
  payPeriodID: number;
  timeEntryIDs: number[];
  timeEntryHourlyIDs: number[];
}): Promise<PortalTimeSummary> {
  const res = await client.post<PortalTimeSummary>(`${P}/timesheet/getTimeSummary`, body);
  return res.data;
}

/** GET /portal/timesheetQuestions — active questions with their children, in order. */
export async function getPortalTimesheetQuestions(): Promise<PortalTimesheetQuestion[]> {
  const res = await client.get<PortalTimesheetQuestion[]>(`${P}/timesheetQuestions`);
  return Array.isArray(res.data) ? res.data : [];
}

/** POST /portal/timesheet/mealWaiverDates { payPeriodID } */
export async function getPortalMealWaiverDates(payPeriodID: number): Promise<PortalMealWaiverDates> {
  const res = await client.post<PortalMealWaiverDates>(`${P}/timesheet/mealWaiverDates`, { payPeriodID });
  const d = res.data;
  return {
    waiverDates: Array.isArray(d?.waiverDates) ? d.waiverDates : [],
    penaltyDates: Array.isArray(d?.penaltyDates) ? d.penaltyDates : [],
    perDay: Array.isArray(d?.perDay) ? d.perDay : [],
  };
}

/** POST /portal/timesheet — submit the week (409 OPEN_PUNCH while a punch is open). */
export async function submitPortalTimesheet(body: PortalTimesheetSubmitBody): Promise<{ success: boolean; timesheetID?: number }> {
  const res = await client.post(`${P}/timesheet`, body);
  return res.data;
}

/** PUT /portal/timeEntryHourly/:id — the WHOLE row (PUT replaces it). */
export async function updatePortalHourly(timeEntryHourlyID: number, body: Record<string, unknown>): Promise<void> {
  await client.put(`${P}${PUNCH_URL.update(timeEntryHourlyID)}`, body);
}

/** POST /portal/timeEntryHourly — a punch added by hand (carries its clientPunchID). */
export async function addPortalHourly(body: Record<string, unknown>): Promise<PortalPunchAddResponse> {
  const res = await client.post<PortalPunchAddResponse>(`${P}${PUNCH_URL.add}`, body);
  return res.data;
}

/** DELETE /portal/timeEntryHourly/:id */
export async function deletePortalHourly(timeEntryHourlyID: number): Promise<void> {
  await client.delete(`${P}${PUNCH_URL.update(timeEntryHourlyID)}`);
}

/* ------------------------------------------------------------------ Time Off (App-3) */

/** GET /portal/timeOff/balance — 422 when no active policy is assigned. */
export async function getPortalTimeOffBalance(): Promise<PortalTimeOffBalance[]> {
  const res = await client.get<PortalTimeOffBalance[]>(`${P}/timeOff/balance`);
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /portal/timeOff/requestOptions?startDate&endDate (YYYY-MM-DD). */
export async function getPortalTimeOffRequestOptions(startYmd: string, endYmd: string): Promise<PortalTimeOffRequestOptions> {
  const res = await client.get<PortalTimeOffRequestOptions>(`${P}/timeOff/requestOptions`, {
    params: { startDate: startYmd, endDate: endYmd },
  });
  const d = res.data;
  return {
    ...d,
    categories: Array.isArray(d?.categories) ? d.categories : [],
    excludedDates: Array.isArray(d?.excludedDates) ? d.excludedDates : [],
    blockedPastDates: Array.isArray(d?.blockedPastDates) ? d.blockedPastDates : [],
    earliestRequestDate: d?.earliestRequestDate ?? null,
  };
}

/** POST /portal/timeOff/requests — 201 with the created request; 400 { message, code?, dates?, blocking? }. */
export async function createPortalTimeOffRequest(body: PortalTimeOffRequestBody): Promise<PortalTimeOffRequest> {
  const res = await client.post<PortalTimeOffRequest>(`${P}/timeOff/requests`, body);
  return res.data;
}

/** GET /portal/timeOff/requests/:id */
export async function getPortalTimeOffRequest(timeOffRequestID: number): Promise<PortalTimeOffRequest> {
  const res = await client.get<PortalTimeOffRequest>(`${P}/timeOff/requests/${timeOffRequestID}`);
  return res.data;
}

/** POST /portal/timeOff/requests/:id/cancel — 400 once that week is submitted or exported. */
export async function cancelPortalTimeOffRequest(timeOffRequestID: number): Promise<void> {
  await client.post(`${P}/timeOff/requests/${timeOffRequestID}/cancel`);
}

/** GET /portal/timeOff/ledger/:categoryId?offset&limit — the unified accrual / usage history. */
export async function getPortalTimeOffLedger(categoryID: number, offset: number, limit: number): Promise<PortalTimeOffLedgerPage> {
  const res = await client.get<PortalTimeOffLedgerPage>(`${P}/timeOff/ledger/${categoryID}`, { params: { offset, limit } });
  const d = res.data;
  return {
    currentBalanceHours: Number(d?.currentBalanceHours ?? 0),
    pending: Array.isArray(d?.pending) ? d.pending : [],
    items: Array.isArray(d?.items) ? d.items : [],
    total: Number(d?.total ?? 0),
    offset: Number(d?.offset ?? offset),
    limit: Number(d?.limit ?? limit),
  };
}

/**
 * GET /portal/activity?payPeriodID=&weeks= — the employee's activity feed for `payPeriodID`
 * (default: this week) and the `weeks - 1` (1..8) before it, newest first. 400
 * INVALID_PAY_PERIOD / INVALID_WEEKS, 404 PAY_PERIOD_NOT_FOUND.
 */
export async function getPortalActivity(opts: { payPeriodID?: number | null; weeks?: number } = {}): Promise<PortalActivityResponse> {
  const params: Record<string, number> = {};
  if (opts.payPeriodID != null) params.payPeriodID = opts.payPeriodID;
  if (opts.weeks != null) params.weeks = opts.weeks;
  const res = await client.get(`${P}/activity`, { params });
  return normalizeActivity(res.data);
}
